/* eslint-disable no-param-reassign,no-case-declarations */
import axios from 'axios';
import * as alerts from '@redux/utils/alerts-interceptors';
import * as authActions from '@redux/actions/auth';
import { store } from '@redux/storeConfig/store';
import { MySwal } from '@src/utility/sweetalert2-config';
import { appIntl } from 'utility/context/IntlGlobalProvider';

export interface ConfigMessages {
  object: string;
  name?: string;
  customMessage?: string;
  customMessageError?: string;
  action?: 'Create' | 'Delete' | 'Edit' | 'Get' | 'Custom' | 'Leave' | 'Import';
  genderType?: 'PluralFemale' | 'SingularFemale' | 'PluralMale' | 'SingularMale';
  successTimer?: number;
  useSuccessTimer?: boolean;
  errorTimer?: number;
}

export interface ConfigActions {
  successAction?: Function;
  errorAction?: Function;
}

export const redirectTo = (document: any, path: string) => {
  document.location = path;
  // window.history.pushState({}, '', path);
  // window.location.reload();
};

// Espera a que termine cualquier rotación de token en curso (forceRefresh tras
// editar perfil/permisos propios, o el refresh periódico). Resuelve cuando
// auth.isRefreshing vuelve a false, o tras un timeout de seguridad.
const waitForRefreshToFinish = (timeoutMs = 5000): Promise<void> =>
  new Promise((resolve) => {
    const state: any = store.getState();
    if (!state?.auth?.isRefreshing) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      unsubscribe();
      resolve();
    }, timeoutMs);
    const unsubscribe = store.subscribe(() => {
      const s: any = store.getState();
      if (!s?.auth?.isRefreshing) {
        clearTimeout(timer);
        unsubscribe();
        resolve();
      }
    });
  });

export const handleSuccess = (
  response: any,
  configMessage: ConfigMessages | undefined,
  configAction: ConfigActions | undefined
) => {
  if (configMessage) {
    const { successTimer = 4000, useSuccessTimer = true } = configMessage;
    const alertObject: alerts.AlertsConfig = {
      object: appIntl().formatMessage({
        id: configMessage.object
      }),
      name: configMessage.name,
      genderType: configMessage.genderType,
      successTimer: !!useSuccessTimer ? successTimer : undefined,
      customMessage: configMessage.customMessage
    };

    switch (configMessage.action) {
      case 'Create':
        alerts.showSuccessCreatedMessage(alertObject);
        break;
      case 'Delete':
        alerts.showSuccessDeletedMessage(alertObject);
        break;
      case 'Edit':
        alerts.showSuccessEditedMessage(alertObject);
        break;
      case 'Leave':
        alerts.showSuccessLeavedMessage(alertObject);
        break;
      case 'Custom':
        if (configMessage.customMessage) alerts.showSuccessCustomMessage(alertObject);
        break;
    }
  }
  if (configAction?.successAction) configAction.successAction(response.data);
  // HttpWebtrack.setConfigMessage(undefined);
  // HttpWebtrack.setConfigActions(undefined);
  return response;
};

export const handleError = async (
  error: any,
  configMessage: ConfigMessages | undefined,
  configAction: ConfigActions | undefined
) => {
  const Toast = MySwal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 6000
  });

  // 401: puede deberse a (a) sesión revocada/expirada -> limpiar identidad,
  // o (b) carrera con una rotación propia de token (forceRefresh tras editar
  // perfil/permisos propios o fijar home screen): el JWT viejo se invalida en
  // backend mientras la request iba en vuelo. Detectamos (b) por dos vías:
  //   - hay rotación en curso (auth.isRefreshing === true), o
  //   - el token actual en store difiere del usado en el request fallido
  //     (la rotación ya terminó pero la request salió con el token viejo).
  // En cualquiera de esos casos esperamos a que termine la rotación y
  // reintentamos una sola vez con el token nuevo antes de cerrar sesión.
  if (error?.response?.status === 401 && !error?.config?._sessionRetry) {
    const stateNow: any = store.getState();
    const currentToken: string | null = stateNow?.auth?.token ?? null;
    const wasAuthenticated = !!currentToken;
    const isRotating = !!stateNow?.auth?.isRefreshing;

    const usedAuth =
      (error.config?.headers?.Authorization as string | undefined) ||
      (error.config?.headers?.authorization as string | undefined) ||
      '';
    const usedToken = usedAuth.startsWith('Bearer ') ? usedAuth.slice(7) : '';
    const tokenWasRotated = !!currentToken && !!usedToken && currentToken !== usedToken;

    if (wasAuthenticated && (isRotating || tokenWasRotated)) {
      if (isRotating) await waitForRefreshToFinish();
      const newToken = (store.getState() as any)?.auth?.token;
      if (newToken && error?.config) {
        try {
          const retryConfig = {
            ...error.config,
            _sessionRetry: true,
            headers: {
              ...(error.config.headers || {}),
              Authorization: `Bearer ${newToken}`
            }
          };
          const retryResponse = await axios.request(retryConfig);
          return handleSuccess(retryResponse, configMessage, configAction);
        } catch (retryError) {
          // cae al manejo normal de 401 abajo
          error = retryError;
        }
      }
    }

    if (error?.response?.status === 401) {
      const stillAuth = !!(store.getState() as any)?.auth?.token;
      if (stillAuth) {
        Toast.fire({
          icon: 'warning',
          title: appIntl().formatMessage({ id: 'profile.activeSessions.sessionEndedRemotely' })
        });
        store.dispatch(authActions.clearIdentity());
      }
    }
  }

  switch (error?.response?.status) {
    // case 403:
    //   Toast.fire({
    //     icon: 'error',
    //     title: 'No Autorizado'
    //   });
    //   setTimeout(() => {
    //     if (error?.response.data?.source !== 'login') {
    //       localStorage.clear();
    //       redirectTo(document, `${API_BASE_URL}/`);
    //     }
    //   }, 2000);
    //   break;
    // case 404:
    //   Toast.fire({
    //     icon: 'error',
    //     title: 'Servicio no encontrado!'
    //   });
    //   break;
    default:
      if (configMessage) {
        const alertObject: alerts.AlertsConfig = {
          object: appIntl().formatMessage({
            id: configMessage.object
          }),
          name: configMessage.name,
          genderType: configMessage.genderType,
          errorTimer: configMessage.action === 'Custom' ? 4000 : configMessage.errorTimer,
          customMessageError: configMessage.customMessageError
        };

        switch (configMessage.action) {
          case 'Create':
            alerts.showFailCreatedMessage(alertObject);
            break;
          case 'Delete':
            alerts.showFailDeletedMessage(alertObject);
            break;
          case 'Leave':
            alerts.showFailLeavedMessage(alertObject);
            break;
          case 'Edit':
            alerts.showFailEditedMessage(alertObject);
            // case 'Get':
            //   alerts.showErrorAlertFailChanges({
            //     error: { errorMessage: 'Error al obtener el listado' }
            //   });
            break;
          case 'Custom':
            if (configMessage.customMessageError) alerts.showErrorCustomMessage(alertObject);
            break;
        }
      }
      break;
  }
  if (configAction?.errorAction) configAction.errorAction(error);
  // HttpWebtrack.setConfigMessage(undefined);
  // HttpWebtrack.setConfigActions(undefined);
  return Promise.reject(error);
};
